// src/features/rrhh/horarios/components/HorariosFilters.jsx
import React, { useState, useCallback } from "react";
import {
  FormControl,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

/* ─── Shared sx tokens ──────────────────────────── */

const PILL_SX = {
  height: "5vh",
  minHeight: 36,
  bgcolor: "neutral.50",
  borderRadius: "50px",
  boxShadow: "inset 0px 0px 2px rgba(96, 96, 113, 0.5)",
};

const SELECT_SX = {
  ...PILL_SX,
  minWidth: 180,
  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
  "& .MuiSelect-select": {
    fontSize: 13,
    fontWeight: 500,
    py: 0,
    display: "flex",
    alignItems: "center",
  },
};

/* ─── Component ─────────────────────────────────── */

/**
 * Horizontal filter bar for the Horarios page.
 *
 * Props:
 *  - onSearch      : (value: string) => void   (parent debounces this)
 *  - hireDate      : string  (YYYY-MM-DD or "")
 *  - onHireDate    : (value: string) => void
 *  - branchId      : number|""
 *  - onBranchId    : (value: number|"") => void
 *  - areaId        : number|""
 *  - onAreaId      : (value: number|"") => void
 *  - sectionId     : string|""
 *  - onSectionId   : (value: string|"") => void
 *  - branches      : Array<{ id, name }>
 *  - areas         : Array<{ id, name }>
 *  - sections      : Array<{ id, name }>
 */
export default React.memo(function HorariosFilters({
  onSearch,
  branchId = "",
  onBranchId,
  areaId = "",
  onAreaId,
  sectionId = "",
  onSectionId,
  branches = [],
  areas = [],
  sections = [],
}) {
  // Local state for the search input – avoids re-rendering the parent on every keystroke
  const [localSearch, setLocalSearch] = useState("");

  const handleSearch = useCallback(
    (e) => {
      const v = e.target.value;
      setLocalSearch(v);   // instant visual feedback
      onSearch?.(v);        // notify parent (parent debounces)
    },
    [onSearch],
  );

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      sx={{ width: "100%", flexWrap: "wrap", rowGap: 1 }}
    >
      {/* ── Search field (Nombre / DNI) ── */}
      <TextField
        value={localSearch}
        onChange={handleSearch}
        placeholder="Buscar por nombre o DNI…"
        size="small"
        sx={{
          flex: 1,
          width: "50%",
          ...PILL_SX,
          "& .MuiOutlinedInput-root": {
            borderRadius: "50px",
            height: "100%",
            fontSize: 13,
          },
          "& .MuiOutlinedInput-notchedOutline": { border: "none" },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: "primary.900", fontSize: 20 }} />
            </InputAdornment>
          ),
        }}
      />

      {/* ── Sucursal dropdown ── */}
      <FormControl size="small">
        <Select
          displayEmpty
          value={branchId}
          onChange={(e) => onBranchId?.(e.target.value)}
          sx={SELECT_SX}
          renderValue={(v) => {
            if (!v) return <Typography sx={{ fontSize: 13, color: "text.secondary" }}>Sucursal</Typography>;
            const found = branches.find((b) => b.id === v);
            return <Typography sx={{ fontSize: 13 }}>{found?.name ?? v}</Typography>;
          }}
        >
          <MenuItem value="">
            <em>Todas</em>
          </MenuItem>
          {branches.map((b) => (
            <MenuItem key={b.id} value={b.id}>
              {b.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* ── Área dropdown ── */}
      <FormControl size="small">
        <Select
          displayEmpty
          value={areaId}
          onChange={(e) => onAreaId?.(e.target.value)}
          sx={SELECT_SX}
          renderValue={(v) => {
            if (!v) return <Typography sx={{ fontSize: 13, color: "text.secondary" }}>Área</Typography>;
            const found = areas.find((a) => a.id === v);
            return <Typography sx={{ fontSize: 13 }}>{found?.name ?? v}</Typography>;
          }}
        >
          <MenuItem value="">
            <em>Todas</em>
          </MenuItem>
          {areas.map((a) => (
            <MenuItem key={a.id} value={a.id}>
              {a.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* ── Sector dropdown ── */}
      <FormControl size="small">
        <Select
          displayEmpty
          value={sectionId}
          onChange={(e) => onSectionId?.(e.target.value)}
          sx={SELECT_SX}
          renderValue={(v) => {
            if (!v) {
              return (
                <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                  Sector
                </Typography>
              );
            }
          
            const found = sections.find((s) => String(s.id) === String(v));
            return <Typography sx={{ fontSize: 13 }}>{found?.name ?? v}</Typography>;
          }}
        >
          <MenuItem value="">
            <em>Todas</em>
          </MenuItem>
        
          {sections.map((s) => (
            <MenuItem key={s.id} value={s.id}>
              {s.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Stack>
  );
});
